import dayjs from 'dayjs'

export const ATTENDANCE_COLUMN_MAPPERS = {
  'Attendance': (att) => att.Attendance || '-',
  'First Clock-In': (att) => att['First Clock-In'] || '-',
  'Last Clock-Out': (att) => att['Last Clock-Out'] || '-',
  'Completed Working Hour': (att) => att['Completed Working Hour'] || '-',
  'Working Hours': (att) => att['Completed Working Hour'] || '-',
  'Planned Working Hour': (att) => att['Planned Working Hour'] || '-',
  'Overtime': (att) => att.Overtime || '-',
  'Delay In Shift': (att) => att['Delay In Shift'] || '-',
  'Breaks Count': (att) => att['Breaks Count'] || '0',
  'Distance (km)': (att) => att['Distance (km)'] || '0',
  'Clock-In Image': (att) => att['Clock-In Image'] || '-',
  'Clock-Out Image': (att) => att['Clock-Out Image'] || '-',
  'Attendance Status': (att) => att.Attendance || '-',
  'Date': (att, date) => dayjs(date).format('DD/MM/YYYY')
}

export const calculateStatus = (actualWorkingHrs, settings) => {
  if (!actualWorkingHrs || !settings) return ''
  const [hour, minute, second] = actualWorkingHrs.split(':').map(Number)
  const actualHours = (hour * 60 + minute + second / 60).toFixed(2)
  const workingHours = (settings.workingHours || 0) * 60
  const workHourThreshold = settings.fullDayThreshold || 100
  const orgWorkingHours = workingHours * (workHourThreshold / 100)
  const halfActualHours = (orgWorkingHours * (settings.halfDayThreshold || 0)) / 100
  const overTimeThresholdTime = workingHours + (workingHours * (settings.overTimeThreshold || 0)) / 100
  
  if (actualHours >= overTimeThresholdTime) return 'Over Time'
  if (actualHours >= halfActualHours && actualHours < orgWorkingHours) return 'Half Day'
  if (actualHours >= orgWorkingHours && actualHours < overTimeThresholdTime) return 'Full Day'
  return ''
}

export const overTimeDuration = (actualWorkingHrs, settings) => {
  if (!actualWorkingHrs || !settings) return '-'
  const [hour, minute, second] = actualWorkingHrs.split(':').map(Number)
  const actualHours = (hour * 60 + minute + second / 60).toFixed(2)
  const orgWorkingHours = (settings.workingHours || 0) * 60
  const duration = actualHours - orgWorkingHours
  if (duration <= 0) return '-'
  const hours = Math.floor(duration / 60)
  const minutes = Math.floor(duration % 60)
  const seconds = Math.floor(((duration % 60) % 1) * 60)
  return `${Math.abs(hours)}h ${minutes}min ${seconds}sec`
}

export const calculateHoursWorked = (checkInOutData, settings, s3Url = '') => {
  let firstClockIn, lastClockOut, clockInCount = 0
  const images = []
  
  checkInOutData.forEach((record) => {
    if (record.status === 'CHECKED_IN') {
      clockInCount++
      if (!firstClockIn) firstClockIn = record
    } else if (record.status === 'CHECKED_OUT') {
      lastClockOut = record
    }
    if (record.selfieUrl) {
      images.push({
        image: `${s3Url}${record.selfieUrl}`,
        status: record.status,
        time: record?.time
      })
    }
  })

  let delayInShift = '-'
  if (firstClockIn && settings?.shiftStartTime) {
    const clockInTimeFormat = dayjs(firstClockIn.time).format('HH:mm')
    const shiftStartTime = dayjs(settings.shiftStartTime, 'HH:mm')
    const firstClockInTime = dayjs(clockInTimeFormat, 'HH:mm')
    const diffHours = firstClockInTime.diff(shiftStartTime, 'hour')
    const diffMinutes = firstClockInTime.diff(shiftStartTime, 'minute') % 60

    if (diffHours > 0 || diffMinutes > 0) {
      delayInShift = `${diffHours}h ${diffMinutes}min`
    }
  }

  return {
    firstClockInTime: firstClockIn?.time,
    lastClockOutTime: lastClockOut?.time,
    images,
    clockInImage: firstClockIn?.selfieUrl ? `${s3Url}${firstClockIn.selfieUrl}` : '',
    clockOutImage: lastClockOut?.selfieUrl ? `${s3Url}${lastClockOut.selfieUrl}` : '',
    delayInShift,
    clockInCount
  }
}

/**
 * Transforms raw attendance data and user list into a structured map for reports
 */
export const processAttendanceItems = (users, attendanceData, settings, s3Url = '') => {
  return users.map((u) => {
    const userId = u._id || u.id
    const userAttendance = attendanceData.filter((a) => (a.userId?._id === userId || a.userId === userId))
    const attendanceMap = {}
    
    userAttendance.forEach((a) => {
      const date = dayjs(a.attendanceDateUTC || a.attendanceDate).format('YYYY-MM-DD')
      
      const { 
        firstClockInTime, 
        lastClockOutTime, 
        delayInShift, 
        clockInCount,
        clockInImage,
        clockOutImage 
      } = calculateHoursWorked(a.checkInOutData || [], settings, s3Url)

      let workingHrs = a.workingHour || '-'
      if (a.actualWorkingHrs) {
        const [h, m, s] = a.actualWorkingHrs.split(':')
        workingHrs = `${h}h ${m}min ${s}sec`
      }

      attendanceMap[date] = {
        'Attendance': a.attendanceStatus || '-',
        'First Clock-In': firstClockInTime ? dayjs(firstClockInTime).format('hh:mm A') : '-',
        'Last Clock-Out': lastClockOutTime ? dayjs(lastClockOutTime).format('hh:mm A') : '-',
        'Completed Working Hour': workingHrs,
        'Overtime': overTimeDuration(a.actualWorkingHrs, settings),
        'Delay In Shift': delayInShift,
        'Breaks Count': clockInCount > 0 ? (clockInCount - 1 || 'No break') : '0',
        'Distance (km)': a.distance || '0',
        'Planned Working Hour': settings?.workingHours ? `${settings.workingHours}h` : '-',
        'Clock-In Image': clockInImage || '-',
        'Clock-Out Image': clockOutImage || '-'
      }
    })
    
    return {
      ...u,
      name: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
      orgEmployeeId: u.employeeId || u.orgEmployeeId,
      attendance: attendanceMap
    }
  })
}

/**
 * Column definition for supported attendance reports
 */
export const ATTENDANCE_VALID_COLS = [
  'Attendance',
  'First Clock-In',
  'Last Clock-Out',
  'Planned Working Hour',
  'Completed Working Hour',
  'Overtime',
  'Delay In Shift',
  'Breaks Count',
  'Distance (km)',
  'Clock-In Image',
  'Clock-Out Image'
]
